import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import OverrideEditButton from './OverrideEditButton';
import { findTable } from '../../../config/dataTables.registry';
import { isPctRateType } from './invoiceLineItemShared';
import type {
  InvoiceEstimate,
  InvoiceProjection,
  EstimatedLine,
  EstimateSource,
} from '../../../services/invoiceReconcile.service';

export interface InvoiceEstimateModalProps {
  pronumber: number | string;
  loading: boolean;
  error: string | null;
  estimate: InvoiceEstimate | null;
  /** Load the projection into the invoice-line form (staged, unsaved, highlighted). */
  onStage: (estimate: InvoiceEstimate) => void;
  /** Stage ONE accessorial's proposal into its own editor. Separate from `onStage`
   *  because the two land in different places: the Invoicing tab's form holds the MAIN
   *  invoice's lines, so an accessorial's changes can only be staged into the Accessorial
   *  modal for that document. Absent => the accessorial panels offer no stage action
   *  (their rows are still readable, and overridable via `onOverride`). */
  onStageAccessorial?: (group: InvoiceLineGroup) => void;
  /** Override a line item from here, rather than making the user close, hunt for the
   *  row, and re-estimate. The moment someone reads "invoice $0.00 vs quote $110.00" is
   *  the moment they know why it's zero — that's when to capture the reason.
   *
   *  `invoiceRecno` names the document the decision is taken on, and is NOT provenance:
   *  it resolves the QUOTE the override binds to (migration 526). Omitted => the main
   *  invoice. Accessorial rows used to be read-only, because an override keyed on
   *  (pro, line item) alone would have silently changed what the main invoice projects
   *  too; with the quote in the key that restriction is gone. */
  onOverride?: (
    liitem: string, existingRecno: number | null, invoiceRecno?: number | null,
  ) => void;
  /** Re-agree a drifted override at what the charge is worth now — the way out of the
   *  "needs review" state that refuses approval. Omitted => the state still SHOWS, so a
   *  reader can see why a save is being refused even without the grant to clear it. */
  onReverify?: (liitem: string) => void;
  /** Recompute from scratch rather than reading the published estimate. Offered whenever
   *  a cached projection is on screen — a cached figure is as of a date, and the reader
   *  needs a way to ask for "now" without guessing whether it matters. */
  onRefresh?: () => void;
  onClose: () => void;
}

/** "2026-09-15" -> "09/15/2026". Dates arrive from the cache as ISO; the rest of this
 *  screen (and every estimate_basis string in it) is US-formatted. */
const asOfLabel = (iso?: string | null): string | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : null;
};

/**
 * WHEN this projection was computed, phrased the way the question actually gets asked:
 * "I just changed something — is it in here?"
 *
 * Distinct from `estimateAsOf`, and the distinction is easy to lose. `estimateAsOf` is
 * the date the projections were made FROM (what an undated chassis window was assumed
 * to end on) — it describes the FIGURES. This describes the ROW: with the sweep on a
 * 15-minute cadence, an edit made two minutes ago is very likely not reflected yet, and
 * a date alone cannot tell anyone that.
 *
 * `syncedAt` is stored in Central without an offset and the browser parses a naive ISO
 * string as local time, which lands right for GWI and wrong for anyone elsewhere. A bad
 * offset would produce a confidently wrong "2 hours ago", so anything outside a sane
 * window falls back to the clock time, which reads correctly either way.
 */
const syncedLabel = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;

  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 0 || mins > 180) {
    return then.toLocaleString(undefined, {
      month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  }
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
};

/** When a snapshot was taken. ALWAYS absolute and always with the year, unlike
 *  `syncedLabel`: "3 minutes ago" asks the reader to judge whether a figure is current,
 *  which is the wrong question about a record that is final, and an archive is read
 *  years after it was written. Same naive-timestamp caveat as `syncedAt` — the server
 *  sends Central with no offset and the browser reads it as local. */
const archivedLabel = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return then.toLocaleString(undefined, {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const money = (v: number) => `$${(v ?? 0).toFixed(2)}`;

/** Below this, two TOTALS bill the same cents. */
const HALF_CENT = 0.005;
/** Below this, two RATES are the same stored figure — `lirate` is decimal(10,5), so a
 *  change in the fifth decimal is a real change, and judging rates by the half-cent rule
 *  would leave 0.51800 -> 0.52100 showing two different numbers with nothing marking
 *  either as superseded. That only became visible once rates stopped rounding to cents. */
const HALF_FIFTH_DECIMAL = 0.000005;

/**
 * A rate at the precision it is STORED at. `invoice_lineitem.lirate` is decimal(10,5) and
 * a per-mile fuel rate uses all of it, so rounding to cents does not show a shorter
 * version of the figure — it shows a DIFFERENT one. A line proposing 0.518 against an
 * invoice billing 0.518 rendered both as "$0.52" while the Change column said "Update",
 * which reads as the engine wanting a rate it does not want (Lee, pro 5055242,
 * 2026-10-20) — and the Quote Charges strip two panels away was already showing 0.518.
 *
 * Mirrors v1 `padrate` (and `plan._padrate`, which compares rates by this same rule):
 * five decimals, trailing zeros trimmed to a two-decimal minimum. 245 -> "245.00",
 * 0.51800 -> "0.518", 52.30000 -> "52.30".
 */
const padRate = (v: number): string => {
  const [intPart, frac = ''] = (v ?? 0).toFixed(5).split('.');
  return `${intPart}.${frac.slice(0, 2)}${frac.slice(2).replace(/0+$/, '')}`;
};

/**
 * A RATE in its own unit. A PCT % line's rate is a percentage — a fuel surcharge of 68.7%
 * shown as "$68.70" is not a smaller version of the truth, it's a different quantity, and
 * on a screen whose whole job is comparing rates it invites exactly the wrong conclusion.
 *
 * TOTALS are always money: PCT % or not, the line still bills dollars — and a total IS
 * rounded to cents, because that is what gets billed.
 */
const rate = (v: number, rateType: string | null | undefined) =>
  isPctRateType(rateType) ? `${padRate(v)}%` : `$${padRate(v)}`;

/**
 * The money columns come in two pairs — what the invoice bills now, and what the quote
 * or derivation says it should. Rate and total belong together within each pair, so each
 * gets a rule down either side: four bare numeric columns in a row read as one
 * undifferentiated block, and the comparison the screen exists to make is between the
 * PAIRS, not between adjacent columns.
 */
const GROUP_L = 'border-l border-slate-300 dark:border-gray-600';
const GROUP_R = 'border-r border-slate-300 dark:border-gray-600';

/** Row treatment per source. Every PROPOSED CHANGE gets the one yellow the Invoicing
 *  tab's suggested charges use, so "this needs a decision" reads the same in both places
 *  (Lee, 2026-10-23); WHICH change it is stays on the badge — green add, blue update, red
 *  remove. Everything else is plain: most of the invoice is unchanged, and tinting every
 *  row would leave nothing for the changes to stand out against. `held` is plain too —
 *  an override already decided it, and the same yellow would claim it still needs one. */
const CHANGE_ROW = 'bg-amber-50 dark:bg-amber-900/30';
const ROW_CLASS: Record<EstimateSource, string> = {
  existing: '',
  matched: '',
  add: CHANGE_ROW,
  update: CHANGE_ROW,
  remove: CHANGE_ROW,
  held: '',
};

/** The three sources that are PROPOSED CHANGES. `matched` and `held` are informational
 *  — one says the line is already right, the other that an override stopped a proposal —
 *  and counting either overstates what the button would stage. Mirrors the backend's
 *  `EstimatedLine.is_change`, the predicate behind `estimate.hasChanges`; keeping the two
 *  in step is what stops the header ("6 proposed changes") contradicting the empty state
 *  ("No proposed changes") on the same screen. */
const CHANGE_SOURCES: ReadonlySet<EstimateSource> = new Set<EstimateSource>([
  'add',
  'update',
  'remove',
]);

/**
 * "(using today)" — what an undated chassis window was assumed to end on, short enough
 * to sit under the Estimated badge.
 *
 * "today" is said only when it IS today. The sweep republishes every fifteen minutes so
 * a cached projection almost always was built today, but "almost always" is not a thing
 * to print: one read just after midnight, on a row written just before it, and the label
 * is a day wrong about the only fact it exists to state. When the date isn't today it is
 * named outright, and when there's no as-of at all the label says nothing rather than
 * guessing. The full basis ("chassis end date assumed 09/28/2026") stays on hover.
 */
const usingLabel = (iso?: string | null): string => {
  const shown = asOfLabel(iso);
  if (!shown) return '';
  const now = new Date();
  const today = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(
    now.getDate(),
  ).padStart(2, '0')}/${now.getFullYear()}`;
  return shown === today ? '(using today)' : `(using ${shown})`;
};

/**
 * A removal's reason, linked to the record it names when there is one.
 *
 * The reason is deliberately a LABEL rather than a sentence — "Custom Chassis Rule
 * #112", not a paragraph about what rule 112 says. That trade only works if the detail
 * is one click away instead of gone, so the label opens the record in a new tab: the
 * rule itself is a better answer than any retelling of it, and it stays right when the
 * rule changes.
 *
 * `reasonLinkTable` arrives as a `dataTables.registry` key, never a URL, so the route
 * is resolved here — one place owns where Data Management pages live. An unknown key
 * (a table renamed, an older cached row) falls back to plain text rather than a dead
 * link. `?edit=<recno>` is the deep link TablemaintPage already honours, the same one
 * EntityEditLink uses from the order screen.
 */
export const ReasonText: React.FC<{
  reason: string;
  linkTable?: string | null;
  linkRecno?: number | null;
}> = ({ reason, linkTable, linkRecno }) => {
  const target = linkRecno ? findTable(linkTable ?? '') : undefined;
  if (!target) return <>{reason}</>;
  return (
    <a
      href={`${target.route}?edit=${linkRecno}`}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${target.singular ?? target.label} #${linkRecno} in a new tab`}
      className="inline-flex items-center gap-0.5 underline decoration-dotted underline-offset-2 hover:decoration-solid"
    >
      {reason}
      <ArrowTopRightOnSquareIcon className="w-2.5 h-2.5 shrink-0" />
    </a>
  );
};

const BADGE: Record<EstimateSource, { label: string; cls: string }> = {
  // 'existing' is a charge the engine has no opinion about, so it gets no badge — only
  // 'matched' means "checked and correct", and claiming that for an unexamined line
  // would imply a verification that never happened.
  //
  // COLOUR CARRIES MEANING: the three PROPOSED CHANGES own the saturated colours
  // (add/update/remove), because those are what the eye needs to find. 'matched' is
  // confirmation, not an action, so it stays deliberately quiet — a grey chip with a
  // green tick. It previously shared green with 'add', which made a settled line and a
  // new charge look like the same kind of thing.
  existing: { label: '', cls: '' },
  matched: { label: '✓ Matches quote', cls: 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300' },
  add: { label: 'Add', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  update: { label: 'Update', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  remove: { label: 'Remove', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  held: { label: 'Overridden', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
};

/**
 * Is this particular figure being REPLACED?
 *
 * An `update` does not necessarily change every figure on its row. On PRO 5068398 the
 * chassis management rate stays $20.00 and only the day count moves, 15 -> 34 — striking
 * the rate there claims a change that isn't happening, and sends the reader hunting for a
 * new rate that doesn't exist. So each figure is compared against its own proposal rather
 * than the whole row being treated as superseded.
 */
const supersedes = (
  source: EstimateSource,
  current: number | null | undefined,
  proposed: number,
  epsilon: number = HALF_CENT,
): boolean =>
  source === 'update' && current != null && Math.abs(current - proposed) >= epsilon;

/**
 * Did the rate's UNIT change?
 *
 * A rate type is not decoration — DAYS, FLAT, MIN and PCT % are different billing bases,
 * so "$200.00 DAYS" and "$200.00 FLAT" are different line items that happen to print the
 * same number. `rate()` renders only the percent sign, so on any pairing that wasn't
 * PCT-vs-money the difference was invisible: PRO 5107809's CHASSIS MANAGEMENT line
 * proposed DAYS -> FLAT at the same $200.00 x 1, and the row showed an Update badge
 * beside four identical figures with nothing on screen to say what it wanted to change.
 * Read as "the engine is proposing nothing", which is how it got reported.
 *
 * Compared case- and space-insensitively because the type is free-ish text in V1
 * (`liratetype` is a varchar), not an enum.
 */
const rateTypeChanged = (l: EstimatedLine): boolean =>
  l.source === 'update' &&
  l.currentRate != null &&
  (l.currentRateType ?? '').trim().toUpperCase() !== (l.rateType ?? '').trim().toUpperCase();

/** A rate is superseded by a change of UNIT as well as of value: 68.7 as a percentage and
 *  $68.70 are the same number and a different quantity, so the old figure is gone even
 *  though it reads the same.
 *
 *  Only the PCT-vs-money flip strikes the figure, not every `rateTypeChanged`: on a
 *  DAYS -> FLAT at the same $200.00 the number genuinely is not being replaced, and
 *  striking it would send the reader hunting for a new rate that doesn't exist — the
 *  same mistake this predicate was split out to avoid. That change is announced under
 *  the badge instead. */
const supersedesRate = (l: EstimatedLine): boolean =>
  supersedes(l.source, l.currentRate, l.rate, HALF_FIFTH_DECIMAL) ||
  (l.source === 'update' &&
    l.currentRate != null &&
    isPctRateType(l.currentRateType) !== isPctRateType(l.rateType));

/** A rate type for display; blank renders as the em dash the line-item grid's own Type
 *  select uses for "no type". */
const rateTypeLabel = (t?: string | null): string => (t ?? '').trim() || '—';

/** A figure on the CURRENT side: muted, em-dash when the line isn't on the invoice, and
 *  struck through once a proposed change supersedes it. `rateType` renders a rate in its
 *  own unit; omit it for a total, which is always money. */
const CurrentFigure: React.FC<{
  value: number | null;
  superseded: boolean;
  rateType?: string | null;
  isRate?: boolean;
}> = ({ value, superseded, rateType, isRate = false }) => {
  if (value == null) return <span className="text-gray-400 dark:text-gray-600">—</span>;
  return (
    <span
      className={
        superseded
          ? 'text-gray-400 line-through dark:text-gray-500'
          : 'text-gray-600 dark:text-gray-400'
      }
    >
      {isRate ? rate(value, rateType) : money(value)}
    </span>
  );
};

/** Which invoice a line sits on, as one comparable key — the same key the backend uses
 *  for its per-invoice breakdown. A charge with no invoice of its own (one that would
 *  CREATE a pending accessorial) lands under 0 on both sides. */
const groupKey = (
  invoiceRecno: number | null | undefined,
  accessorialNum: number | undefined,
) => `${invoiceRecno ?? 0}:${accessorialNum ?? 0}`;

export interface InvoiceLineGroup {
  key: string;
  /** "Main invoice" / "Accessorial #2" */
  label: string;
  /** "8952 · FIVE BELOW", or '' when the document has no billto to report */
  party: string;
  isMain: boolean;
  /** The document itself, so an action on a group can address it. `invoiceRecno` is null
   *  — and `isNew` true — on an accessorial applying would CREATE: there is nothing to
   *  open yet, so it cannot be staged into. */
  invoiceRecno: number | null;
  accessorialNum: number;
  isNew: boolean;
  /** The v1 quote this document prices off — its own, else the main invoice's. */
  quoteNumber: number;
  /** This document prices off a DIFFERENT quote from the main invoice, so its charges
   *  are judged against another rate basis and its overrides are its own. Worth saying
   *  out loud: two panels showing the same line item at different rates look like a
   *  mistake until you know they are quoted separately. */
  ownQuote: boolean;
  lines: EstimatedLine[];
  currentTotal: number;
  projectedTotal: number;
  changeCount: number;
  /** This document is BILLED: its rows are the reconciliation frozen when it was
   *  invoiced (`archivedAt`), or its lines as they stand when none was archived. Nothing
   *  on it can be staged or overridden — the order's open documents carry on. */
  invoiced: boolean;
  archivedAt: string | null;
}

const changesIn = (lines: EstimatedLine[]) =>
  lines.filter((l) => CHANGE_SOURCES.has(l.source)).length;

/**
 * Split a flat estimate into one group per invoice on the order.
 *
 * The estimate ships its lines as ONE roll-up, each tagged with the document it belongs
 * to, plus a per-invoice breakdown carrying that document's own totals and billto. This
 * rejoins the two — and the totals come from the breakdown rather than being re-summed
 * here, so what the screen foots is exactly what the projection computed.
 *
 * Exported and pure so the grouping is testable without rendering a modal.
 */
export function groupLinesByInvoice(estimate: InvoiceEstimate): InvoiceLineGroup[] {
  const lines = estimate.lines ?? [];

  // No per-invoice breakdown on the payload: the roll-up IS the main invoice, which is
  // precisely what it was before the projection covered accessorials at all.
  if (!estimate.invoices?.length) {
    return [{
      key: 'main',
      label: 'Main invoice',
      party: '',
      isMain: true,
      invoiceRecno: estimate.invoiceRecno,
      accessorialNum: 0,
      isNew: false,
      quoteNumber: 0,
      ownQuote: false,
      lines,
      currentTotal: estimate.currentTotal,
      projectedTotal: estimate.projectedTotal,
      changeCount: changesIn(lines),
      invoiced: !!estimate.archived,
      archivedAt: estimate.archivedAt ?? null,
    }];
  }

  const label = (p: InvoiceProjection) =>
    p.isMain
      ? 'Main invoice'
      : p.isNew
        ? `Accessorial #${p.accessorialNum} · would be created`
        : `Accessorial #${p.accessorialNum}`;

  // Number AND name: the number is what someone searches on, the name is what they
  // recognise. Either alone leaves a reader checking whose bill this is somewhere else.
  const party = (p: InvoiceProjection) =>
    p.billtoName
      ? (p.billto ? `${p.billto} · ${p.billtoName}` : p.billtoName)
      : (p.billto ? String(p.billto) : '');

  const mainQuote = estimate.invoices.find((p) => p.isMain)?.quoteNumber ?? 0;

  const buckets = new Map<string, EstimatedLine[]>();
  for (const l of lines) {
    const k = groupKey(l.invoiceRecno, l.accessorialNum);
    const bucket = buckets.get(k);
    if (bucket) bucket.push(l);
    else buckets.set(k, [l]);
  }

  const groups: InvoiceLineGroup[] = estimate.invoices.map((proj) => {
    const k = groupKey(proj.invoiceRecno, proj.accessorialNum);
    const own = buckets.get(k) ?? [];
    buckets.delete(k);
    return {
      key: k,
      label: label(proj),
      party: party(proj),
      isMain: proj.isMain,
      invoiceRecno: proj.invoiceRecno,
      accessorialNum: proj.accessorialNum,
      isNew: proj.isNew,
      quoteNumber: proj.quoteNumber ?? 0,
      ownQuote: !proj.isMain && (proj.quoteNumber ?? 0) !== 0
        && (proj.quoteNumber ?? 0) !== mainQuote,
      lines: own,
      currentTotal: proj.currentTotal,
      projectedTotal: proj.projectedTotal,
      // A billed document's frozen suggestions are history, not work: nothing on it
      // can be staged, so none of them counts toward what the reader can act on.
      changeCount: proj.invoiced ? 0 : changesIn(own),
      invoiced: !!proj.invoiced,
      archivedAt: proj.archivedAt ?? null,
    };
  });

  // A line whose invoice isn't in the breakdown would otherwise VANISH — the one failure
  // this screen must never have, because a charge that isn't rendered reads as a charge
  // that isn't owed. Both halves come from the same projection so it shouldn't happen;
  // if it ever does, the line shows on the main invoice rather than nowhere.
  const orphans = [...buckets.values()].flat();
  if (orphans.length) {
    const home = groups.find((g) => g.isMain) ?? groups[0];
    if (home) {
      home.lines = [...home.lines, ...orphans];
      home.changeCount = home.invoiced ? 0 : changesIn(home.lines);
    } else {
      groups.push({
        key: 'orphans',
        label: 'Main invoice',
        party: '',
        isMain: true,
        invoiceRecno: estimate.invoiceRecno,
        accessorialNum: 0,
        isNew: false,
        quoteNumber: 0,
        ownQuote: false,
        lines: orphans,
        currentTotal: estimate.currentTotal,
        projectedTotal: estimate.projectedTotal,
        changeCount: changesIn(orphans),
        invoiced: false,
        archivedAt: null,
      });
    }
  }

  return groups;
}

/** The strip naming the document a table belongs to. Only rendered when the order has
 *  more than one invoice — on a single-invoice order there is nothing to tell apart. */
const groupCaption = (g: InvoiceLineGroup): React.ReactNode => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-gray-200 bg-gray-50 px-3 py-1.5 dark:border-gray-700 dark:bg-gray-900/40">
    <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-label">
      {g.label}
    </span>
    {g.party && <span className="text-[0.6875rem] text-muted">bills {g.party}</span>}
    {/* Billed documents say so on their own panel. The order is billed one invoice at a
        time, so "archived" belongs to the document, not to the whole modal. */}
    {g.invoiced && (
      <span
        className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-gray-700 dark:bg-gray-700 dark:text-gray-200"
        title={g.archivedAt
          ? 'The reconciliation as it stood when this invoice was created. A billed invoice is not reconciled again.'
          : 'Billed before reconciliations were archived, or in V1 — these are its lines as they stand.'}
      >
        {g.archivedAt
          ? `Invoiced · archived ${archivedLabel(g.archivedAt) ?? ''}`.trim()
          : 'Invoiced · not archived'}
      </span>
    )}
    {/* The quote is what the charges on this panel are judged against, so two panels
        showing the same item at different rates stop looking like a mistake. Called out
        only when this document prices off its OWN quote — on the main invoice, and on
        the accessorials that simply inherit it, repeating the number is noise. */}
    {g.ownQuote && (
      <span
        className="ml-auto inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
        title="This document prices off its own quote, so its charges are judged against a different rate basis and its overrides are its own."
      >
        {`Quote ${g.quoteNumber}`}
      </span>
    )}
  </div>
);

interface LinesTableProps {
  lines: EstimatedLine[];
  currentTotal: number;
  projectedTotal: number;
  /** Omitted => the table is READ-ONLY and grows no override column.
   *
   *  Accessorials used to render that way, because an override was keyed on
   *  (pro, line item) and recording one from an accessorial row would silently change
   *  what the main invoice projects too. Migration 526 put the QUOTE in the key, so a
   *  decision taken here binds to the quote this document prices off — the main
   *  invoice's when it shares it, its own when it does not. The restriction is gone. */
  onOverride?: (liitem: string, existingRecno: number | null) => void;
  /** Re-agree a drifted override at what the charge is worth now. Omitted => the
   *  "needs review" state still SHOWS — a reader has to be able to see why approval is
   *  being refused even where they cannot clear it themselves. */
  onReverify?: (liitem: string) => void;
  /** Names the document these lines are on. Omitted on a single-invoice order, where
   *  there is nothing to tell apart and a caption would be noise. */
  caption?: React.ReactNode;
  /** Suffix distinguishing this table from the others on screen, for the accessible
   *  name. The main invoice keeps the bare label it has always had; an accessorial
   *  qualifies its own, so two tables never answer to the same name. */
  labelSuffix?: string;
  /** The date the projections were built FROM (ISO), for labelling estimated rows. One
   *  value for the whole estimate — every projection on an order is made from the same
   *  assumed date — so it is passed in rather than carried per line. */
  estimateAsOf?: string | null;
}

/**
 * One invoice's lines, with its OWN totals.
 *
 * The totals are per document on purpose. An order's accessorial is frequently billed to
 * a different party — the consignee, the steamship line, the chassis provider — so a
 * figure summed across invoices is nobody's bill. There is deliberately no grand total
 * on this screen.
 */
const LinesTable: React.FC<LinesTableProps> = ({
  lines,
  currentTotal,
  projectedTotal,
  onOverride,
  onReverify,
  caption,
  labelSuffix = '',
  estimateAsOf,
}) => {
  const delta = projectedTotal - currentTotal;
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700">
      {caption}
      <div className="overflow-x-auto">
        <table
          className="w-full text-[0.75rem]"
          aria-label={`Estimated invoice lines${labelSuffix}`}
        >
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Line item</th>
              <th className="px-3 py-2 text-left font-medium">GL</th>
              <th className="px-3 py-2 text-right font-medium">Qty</th>
              <th className={`px-3 py-2 text-right font-medium ${GROUP_L}`}>
                Current Rate
              </th>
              <th className={`px-3 py-2 text-right font-medium ${GROUP_R}`}>
                Current Total
              </th>
              <th className={`px-3 py-2 text-right font-medium ${GROUP_L}`}>
                Quote Rate
              </th>
              <th className={`px-3 py-2 text-right font-medium ${GROUP_R}`}>
                Quote Total
              </th>
              <th className="px-3 py-2 text-left font-medium">Change</th>
              {onOverride && <th className="px-3 py-2 w-8" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {lines.map((l, i) => {
              // An ESTIMATED line never wears the green checkmark, even when its
              // projection equals what's billed. "Matches quote" would claim a
              // confirmation the engine could not actually make — the value was
              // assumed from today because the driving date isn't in place yet,
              // and equality with the current figure is a coincidence.
              const badge = l.estimated
                ? {
                    // "Awaiting rate" rather than the usual Estimated when the reason is
                    // an unpublished reading. Both are projections, but this one is the
                    // only thing on the screen that will refuse the save — and a reader
                    // told "Estimated" would have no idea why, nor that it clears itself.
                    label: l.awaitingPublishedRate ? 'Awaiting rate' : 'Estimated',
                    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
                  }
                : // An `existing` line carrying a note is the third state: the engine
                  // looked, reached a definite answer of "nothing", and can say why. It
                  // is not "no opinion" (blank) and not "checked and correct" (the
                  // green tick) — so it gets the quiet grey of a confirmation with a
                  // label that claims no verification. A line that DOES have a proposal
                  // keeps its own badge; its note is extra context, not the verdict.
                  l.source === 'existing' && l.note
                  ? {
                      label: 'Not rated',
                      cls: 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300',
                    }
                  : BADGE[l.source];
              const struck = l.source === 'remove';
              // The rate was checked against the quote and agrees; nothing was found to
              // justify the charge EXISTING. Both are true, so both are shown — the tick
              // keeps its meaning (the rate really does match) and this says what it does
              // not cover. Never a refusal: "no rule found a reason" is an absence of
              // evidence, not evidence of absence, and on test it describes 773 of 800
              // billed stop offs — almost all of them a leg action no rule knows.
              const unevidenced = l.rateMatchedUnevidenced;
              return (
                <tr
                  key={`${l.existingRecno ?? 'new'}-${l.item}-${i}`}
                  className={ROW_CLASS[l.source]}
                >
                  <td className="px-3 py-2 font-medium text-body">
                    <span className={struck ? 'line-through opacity-70' : undefined}>
                      {l.item}
                    </span>
                    {unevidenced && (
                      <span
                        className="ml-1.5 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-wide text-slate-600 dark:bg-gray-700 dark:text-gray-300"
                        title={`The rate matches the quote, but the engine found nothing on this order to charge ${l.item} for. It is not proposing any change — check that the charge belongs.`}
                      >
                        No match found
                      </span>
                    )}
                    {l.description && (
                      <div className="text-[0.625rem] font-normal text-muted">
                        {l.description}
                      </div>
                    )}
                    {l.source === 'held' && l.withheldTotal != null && (
                      <div className="text-[0.625rem] font-normal text-amber-700 dark:text-amber-300">
                        {`Declined ${money(l.withheldTotal)}`}
                      </div>
                    )}
                    {/* Spelled out here rather than left to the badge's tooltip: the
                        questions a note answers ("why is fuel $0.00?", "is this chassis
                        fee already billed somewhere else?") are ones someone is already
                        asking, and an answer you have to hover to find is one they'll
                        raise a ticket for instead. Shown on every source, because the
                        one beside a proposal is the one that changes a decision. */}
                    {l.note && (
                      <div className="mt-0.5 text-[0.625rem] font-normal text-muted">
                        {l.note}
                      </div>
                    )}
                    {/* A REMOVAL is the one proposal whose reasoning the numbers cannot
                        show. An add or an update puts the new figure right there beside
                        the old one; a remove just greys the row out and strikes it
                        through, and every one of the eight ways a chassis fee can stop
                        applying looks identical from the outside — a deliberate
                        do-not-charge arrangement reads exactly like a lost quote rate.
                        Hence the reason in the open rather than in the badge's tooltip:
                        on PRO 5032953 it is the difference between "the engine dropped
                        my $1,330" and "custom chassis charges rule #112 says this bill
                        to isn't invoiced for chassis". */}
                    {l.source === 'remove' && l.reason && (
                      <div className="mt-0.5 text-[0.625rem] font-normal text-red-700 dark:text-red-300">
                        <ReasonText reason={l.reason} linkTable={l.reasonLinkTable}
                                    linkRecno={l.reasonLinkRecno} />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {l.glCode || '—'}
                  </td>
                  {/* Qty sits outside the Current/Quote pairs, so on an update it would
                      silently show the PROPOSED count with nothing to say what it
                      replaced — which is how a chassis fee going 15 days -> 34 reads as
                      a rate change. Show both. */}
                  <td className="px-3 py-2 text-right text-label whitespace-nowrap">
                    {supersedes(l.source, l.currentQty, l.qty) && (
                      <span className="mr-1 text-gray-400 line-through dark:text-gray-500">
                        {l.currentQty}
                      </span>
                    )}
                    {l.qty}
                  </td>
                  {/* CURRENT pair — what the invoice bills today. On a changed
                      row these are superseded, so both are struck through
                      together rather than one number quietly replacing another.

                      A REMOVAL reads its figures from rate/total, not currentRate/
                      currentTotal: the projection leaves the current_* fields NULL and
                      carries the doomed line's own values in rate/total instead. Read
                      literally, that rendered an em dash under Current and the line's
                      $480 under QUOTE — so a charge the quote does not price at all
                      looked like a quote of $480 that the invoice matched exactly. The
                      figures belong on the current side; the quote side gets the dash,
                      because a removal has no quote figure. */}
                  <td className={`px-3 py-2 text-right ${GROUP_L}`}>
                    <CurrentFigure
                      value={struck ? l.rate : l.currentRate}
                      superseded={struck || supersedesRate(l)}
                      isRate
                      rateType={struck ? l.rateType : l.currentRateType}
                    />
                  </td>
                  <td className={`px-3 py-2 text-right ${GROUP_R}`}>
                    <CurrentFigure
                      value={struck ? l.total : l.currentTotal}
                      superseded={struck || supersedes(l.source, l.currentTotal, l.total)}
                    />
                  </td>
                  {/* QUOTE pair — what it should bill. */}
                  {/* Emphasised only when the rate is what changed — bolding an
                      unchanged $20.00 points the reader at the wrong column. */}
                  <td
                    className={
                      `px-3 py-2 text-right ${GROUP_L} ` +
                      (supersedesRate(l)
                        ? 'font-semibold text-blue-700 dark:text-blue-300'
                        : 'text-label')
                    }
                  >
                    {struck
                      ? <span className="text-gray-400 dark:text-gray-600">—</span>
                      : rate(l.rate, l.rateType)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-medium text-body ${GROUP_R}`}
                  >
                    {struck
                      ? <span className="text-gray-400 dark:text-gray-600">—</span>
                      : money(l.total)}
                  </td>
                  <td className="px-3 py-2">
                    {badge.label && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[0.625rem] font-medium ${badge.cls}`}
                        title={l.reason}
                      >
                        {l.source === 'matched' && !l.estimated ? (
                          <>
                            <span className="text-green-600 dark:text-green-400">✓</span>
                            {' Matches quote'}
                          </>
                        ) : (
                          badge.label
                        )}
                      </span>
                    )}
                    {/* The qualifier on an "Estimated" badge belongs WITH the badge, not
                        under the line item. It used to sit on the left as a full amber
                        pill ("Estimated · chassis end date assumed 09/28/2026"), which
                        put the loudest thing on the row next to the quietest fact on it
                        and left the item column carrying two different kinds of message.
                        Here it reads as what it is: a footnote to the verdict beside it.
                        The exact basis stays on hover. */}
                    {l.estimated && (
                      <div
                        className="mt-0.5 text-[0.5625rem] leading-tight text-amber-700 dark:text-amber-300"
                        title={l.estimateBasis}
                      >
                        {l.awaitingPublishedRate
                          // The as-of label would be actively misleading here: nothing
                          // was projected FROM a date, the reading for the date simply
                          // is not out. The basis says which one and how far the
                          // published ones reach.
                          ? 'rate not published yet'
                          : l.source === 'matched' || l.source === 'existing'
                          ? "can't calculate yet"
                          // No as-of should not mean no explanation: fall back to the
                          // line's own basis rather than leaving an amber badge with
                          // nothing to qualify it and a tooltip on an empty element.
                          : usingLabel(estimateAsOf) || l.estimateBasis}
                      </div>
                    )}
                    {/* An Update whose only change is the rate TYPE, said out loud.
                        Every figure on such a row is identical on both sides — the type
                        is the one thing the table never shows — so the badge appeared
                        next to four matching numbers and read as "proposing nothing".
                        That is how PRO 5107809 got reported: CHASSIS MANAGEMENT at
                        1 x $200.00 either way, with DAYS -> FLAT the entire proposal.
                        Same footnote-to-the-verdict placement as the Estimated
                        qualifier above, and the full reason stays on the badge's hover. */}
                    {rateTypeChanged(l) && (
                      <div className="mt-0.5 text-[0.5625rem] leading-tight text-blue-700 dark:text-blue-300">
                        {`${rateTypeLabel(l.currentRateType)} → ${rateTypeLabel(l.rateType)}`}
                      </div>
                    )}
                  </td>
                  {onOverride && (
                    <td className="px-3 py-2 text-right">
                      {/* Only a proposed change is worth overriding — there's
                          nothing to stop on an unchanged line, and a `held` one
                          is already overridden. */}
                      {l.source === 'held' ? (
                        <div className="inline-flex items-center gap-1">
                          {/* The decision was agreed at a figure that has since
                              moved, so nobody has decided about the charge as it
                              now stands — and approval is being refused on exactly
                              that. Say so where the row is, rather than only in the
                              error the save returns. */}
                          {l.overrideNeedsReview && (
                            <span
                              className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                              title={
                                l.overrideStampedTotal != null
                                  ? `Agreed at ${money(l.overrideStampedTotal)}, now ${money(l.withheldTotal ?? 0)}. Re-verify to approve this invoice.`
                                  : 'The charge has changed since this was agreed. Re-verify to approve this invoice.'
                              }
                            >
                              Review
                            </span>
                          )}
                          {l.overrideNeedsReview && onReverify && (
                            <button
                              type="button"
                              onClick={() => onReverify(l.item)}
                              title={`Re-verify ${l.item} at what it is worth now`}
                              aria-label={`Re-verify ${l.item}`}
                              className="rounded border border-amber-400 px-1.5 py-0.5 text-[0.5625rem] font-medium text-amber-800 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-200 dark:hover:bg-amber-900/30"
                            >
                              Re-verify
                            </button>
                          )}
                          {/* An overridden line still needs a way back in — to
                              reword the decision or withdraw it. Same "Edit" as the
                              rejected-charges list below, because it opens the same
                              dialog on the same kind of record; another Override here
                              would suggest overriding something already overridden. */}
                          <OverrideEditButton
                            liitem={l.item}
                            title={l.reason}
                            onClick={() => onOverride(l.item, l.existingRecno)}
                          />
                        </div>
                      ) : (
                        l.source !== 'existing' && (
                          // The same labelled Override button as the suggested charges on
                          // the Invoicing tab: one control means "override this charge"
                          // wherever it appears (Lee, 2026-10-23 — no padlock).
                          <button
                            type="button"
                            onClick={() => onOverride(l.item, l.existingRecno)}
                            title={`Override ${l.item} — keep it as it is and record why`}
                            aria-label={`Override ${l.item}`}
                            className="shrink-0 rounded border border-amber-400 px-1.5 py-0 text-[0.6875rem] font-medium text-amber-800 hover:bg-amber-200 dark:border-amber-600 dark:text-amber-200 dark:hover:bg-amber-800"
                          >
                            Override
                          </button>
                        )
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {/* The totals foot the two money columns they belong to, so the
              current invoice and the projected one are read down the same
              columns as the lines that make them up. */}
          <tfoot className="border-t-2 border-gray-200 dark:border-gray-600">
            <tr aria-label={`Estimate totals${labelSuffix}`}>
              <td
                colSpan={3}
                className="px-3 py-2 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-muted"
              >
                Invoice total
              </td>
              <td className={`px-3 py-2 ${GROUP_L}`} />
              <td
                className={`px-3 py-2 text-right text-gray-600 dark:text-gray-400 ${GROUP_R}`}
              >
                {money(currentTotal)}
              </td>
              <td className={`px-3 py-2 ${GROUP_L}`} />
              <td
                className={`px-3 py-2 text-right font-semibold text-body ${GROUP_R}`}
              >
                {money(projectedTotal)}
              </td>
              {/* The difference sits in the next column along rather than under
                  either total — it belongs to neither, and tucking it beneath one
                  of them implies it does. */}
              <td className="px-3 py-2 whitespace-nowrap">
                {Math.abs(delta) >= 0.01 && (
                  <span
                    className={
                      delta > 0
                        ? 'text-[0.75rem] font-semibold text-emerald-700 dark:text-emerald-400'
                        : 'text-[0.75rem] font-semibold text-red-700 dark:text-red-400'
                    }
                  >
                    {delta > 0 ? '+' : '−'}
                    {money(Math.abs(delta)).slice(1)}
                  </span>
                )}
              </td>
              {onOverride && <td className="px-3 py-2" />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export const InvoiceEstimateModal: React.FC<InvoiceEstimateModalProps> = ({
  pronumber,
  loading,
  error,
  estimate,
  onStage,
  onStageAccessorial,
  onReverify,
  onOverride,
  onRefresh,
  onClose,
}) => {
  // Accessorials start HIDDEN. Most orders have one invoice, and for the ones that don't,
  // the second document is usually somebody else's bill — opening on both would make the
  // reader work out which table is theirs before they can read either.
  const [showAccessorials, setShowAccessorials] = useState(false);

  const groups = useMemo(
    () => (estimate ? groupLinesByInvoice(estimate) : []),
    [estimate],
  );
  const mainGroup = groups.find((g) => g.isMain) ?? groups[0];
  const accessorialGroups = useMemo(
    () => groups.filter((g) => g !== mainGroup),
    [groups, mainGroup],
  );
  const hasAccessorials = accessorialGroups.length > 0;
  // The main invoice is billed while the order still has an open document. What is left
  // to do lives on an accessorial, so the main panel is read-only and the open
  // accessorial opens expanded rather than hiding the only live panel on the screen.
  const mainBilled = !!mainGroup?.invoiced && !estimate?.archived;
  const hasOpenAccessorial = accessorialGroups.some((g) => !g.invoiced);
  useEffect(() => {
    if (mainBilled && hasOpenAccessorial) setShowAccessorials(true);
  }, [mainBilled, hasOpenAccessorial]);
  // Whether staging will actually leave a projected rate blank is the per-line
  // `applyRate` flag — the same one InvoicingView reads when it stages. Derived from
  // the lines rather than restated in prose, because the two answers diverge the moment
  // RECONCILER_ACCRUE is turned on: an accruing estimate WRITES the projected figure,
  // and a hardcoded note would then be telling people to go fill in a rate that had
  // just been filled in for them.
  const stagesBlankRate = estimate?.lines?.some((l) => l.estimated && !l.applyRate)
    ?? false;

  // MAIN-invoice changes only — this is the count the Stage button acts on, and an
  // accessorial change cannot be staged from here. Counting them together would promise
  // a button that does less than it says.
  const changeCount = mainGroup?.changeCount ?? 0;
  const accessorialChanges = accessorialGroups.reduce((n, g) => n + g.changeCount, 0);
  const accessorialSummary = useMemo(() => {
    if (!hasAccessorials) return '';
    const n = accessorialGroups.length;
    const head = n === 1 ? 'accessorial invoice' : `${n} accessorial invoices`;
    const who = n === 1 && accessorialGroups[0].party ? ` · ${accessorialGroups[0].party}` : '';
    const changes = accessorialChanges
      ? ` · ${accessorialChanges} proposed change${accessorialChanges === 1 ? '' : 's'}`
      : '';
    return `${head}${who}${changes}`;
  }, [accessorialGroups, accessorialChanges, hasAccessorials]);

  const noChanges = !!estimate && !estimate.hasChanges && !estimate.skipped;

  // Alt+C closes. Alt+S stays deliberately unbound but the layer still swallows it —
  // otherwise it would fall through and fire Save Changes on the Pro drawer behind
  // this modal, which is emphatically not what "stage" means.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys({ C: onClose }, rootRef, { layer: true });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Invoice Reconciliation"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-scrim/50 p-4"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <ArrowPathIcon className="w-5 h-5 text-blue-300" />
              <span>Invoice Reconciliation</span>
              <span className="text-sm font-normal text-slate-300">· PRO {pronumber}</span>
              {estimate && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[0.625rem] uppercase tracking-wide bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {estimate.saveMode}
                </span>
              )}
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading && (
            <div className="py-10 text-center text-muted">
              Reconciling the invoice…
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-200">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && estimate?.skipped && (
            <div className="py-8 text-center text-label">
              Nothing to estimate — <span className="font-medium">{estimate.skipped}</span>.
            </div>
          )}

          {!loading && !error && noChanges && (
            <div className="mb-3 flex items-center justify-center gap-2 rounded border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-700 dark:bg-green-900/25 dark:text-green-200">
              <CheckCircleIcon className="w-5 h-5 shrink-0" />
              <span>The invoice already carries every charge this order has earned.</span>
            </div>
          )}

          {!loading && !error && estimate && !estimate.skipped && (
            <>
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm text-label">
                  {mainBilled
                    ? 'Main invoice billed'
                    : changeCount === 0
                      ? (hasAccessorials
                          ? 'No proposed changes on the main invoice'
                          : 'No proposed changes')
                      : `${changeCount} proposed change${changeCount === 1 ? '' : 's'}`}
                  {' · chassis days '}
                  <span>{estimate.numChassisDays}</span>
                </p>
                {/* WHEN this was computed, leading — because on a 15-minute cadence the
                    question a reader actually has is "is the change I just made in
                    here?", and only a timestamp answers that.

                    The projection BASIS date follows, but only when some line was
                    actually projected: it explains the figures, and on an estimate with
                    nothing projected it explains nothing and is just another date to
                    parse. */}
                {/* An ARCHIVED estimate replaces the freshness line rather than joining
                    it. "Estimated 3 minutes ago" invites the reader to judge whether the
                    figures are current, which is the wrong question about a snapshot:
                    it is not stale, it is final. */}
                {estimate.archived ? (
                  <p className="text-xs text-muted">
                    Archived{' '}
                    <span className="font-medium">
                      {archivedLabel(estimate.archivedAt) ?? 'when this order was invoiced'}
                    </span>
                    {' — the reconciliation as it stood when this invoice was created. '}
                    A billed invoice is not reconciled again.
                  </p>
                ) : estimate.fromCache && (
                  <p className="flex items-center gap-2 text-xs text-muted">
                    <span>
                      Estimated{' '}
                      <span className="font-medium">
                        {syncedLabel(estimate.syncedAt) ?? 'on the last sweep'}
                      </span>
                      {estimate.hasEstimates && asOfLabel(estimate.estimateAsOf) && (
                        <span> · projected from {asOfLabel(estimate.estimateAsOf)}</span>
                      )}
                    </span>
                    {onRefresh && (
                      <button
                        type="button"
                        onClick={onRefresh}
                        className="inline-flex items-center gap-1 rounded border border-gray-300 px-1.5 py-0.5 text-[0.6875rem] text-label hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                      >
                        <ArrowPathIcon className="w-3 h-3" />
                        Recalculate
                      </button>
                    )}
                  </p>
                )}
              </div>

              {/* ONE TABLE PER INVOICE. The main invoice always shows; accessorials are
                  behind a toggle and start hidden, because on the overwhelming majority
                  of orders there is only the one document and a reader should not have
                  to work out which of two tables is theirs. */}
              {mainGroup && (
                <LinesTable
                  lines={mainGroup.lines}
                  currentTotal={mainGroup.currentTotal}
                  projectedTotal={mainGroup.projectedTotal}
                  // A billed main invoice is read-only: an override changes what the
                  // engine proposes NEXT, and there is no next for a billed document.
                  onOverride={mainGroup.invoiced ? undefined : onOverride}
                  onReverify={mainGroup.invoiced ? undefined : onReverify}
                  caption={hasAccessorials ? groupCaption(mainGroup) : undefined}
                  estimateAsOf={estimate.estimateAsOf}
                />
              )}

              {hasAccessorials && (
                <button
                  type="button"
                  onClick={() => setShowAccessorials((v) => !v)}
                  aria-expanded={showAccessorials}
                  className="mt-2 inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-[0.6875rem] text-label hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  {showAccessorials
                    ? <ChevronDownIcon className="h-3 w-3" />
                    : <ChevronRightIcon className="h-3 w-3" />}
                  {showAccessorials ? 'Hide' : 'Show'} {accessorialSummary}
                </button>
              )}

              {showAccessorials &&
                accessorialGroups.map((g) => (
                  <div key={g.key} className="mt-2">
                    <LinesTable
                      lines={g.lines}
                      currentTotal={g.currentTotal}
                      projectedTotal={g.projectedTotal}
                      caption={groupCaption(g)}
                      labelSuffix={` — ${g.label}`}
                      estimateAsOf={estimate.estimateAsOf}
                      // An override recorded here binds to THIS document's quote
                      // (migration 526), so it no longer leaks onto the main invoice.
                      // Withheld on an accessorial that does not exist yet: there is no
                      // document to decide about, and no recno to record against.
                      onOverride={
                        onOverride && !g.isNew && !g.invoiced && g.invoiceRecno != null
                          ? (item, recno) => onOverride(item, recno, g.invoiceRecno)
                          : undefined
                      }
                      onReverify={g.invoiced ? undefined : onReverify}
                    />
                    {/* An accessorial's changes cannot go into the form behind this modal
                        — that form holds the MAIN invoice's lines — so each document gets
                        its own action, which opens the editor that CAN hold them. Shown
                        only when there is something to stage, and never on an accessorial
                        that does not exist yet: applying would have to create it first,
                        which is a write this modal does not do. */}
                    {onStageAccessorial && g.changeCount > 0 && !g.isNew && !g.invoiced
                      && g.invoiceRecno != null && (
                      <div className="mt-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => onStageAccessorial(g)}
                          className="rounded-md border border-blue-300 px-2 py-1 text-[0.6875rem] font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/30"
                        >
                          {`Stage in ${g.label.toLowerCase()}`}
                        </button>
                      </div>
                    )}
                  </div>
                ))}

              {estimate.rejected?.length > 0 && (
                <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 dark:border-amber-700 dark:bg-amber-900/25">
                  <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-200">
                    Rejected charges
                    {estimate.withheldTotal > 0 && (
                      <span className="ml-2 normal-case tracking-normal">
                        {`— ${money(estimate.withheldTotal)} not billed`}
                      </span>
                    )}
                  </p>
                  {/* Charges overridden with no invoice line — they have no row above, so
                      without this they would silently vanish the moment they were
                      rejected, which reads as the override having failed. */}
                  {estimate.rejected.map((r) => (
                    <div
                      key={r.item}
                      className="mt-1 flex items-start justify-between gap-2 text-[0.6875rem] text-amber-800 dark:text-amber-200"
                    >
                      <span className="flex-1">
                        <span className="font-semibold">{r.item}</span>
                        {' — not billed on this pro. '}
                        {r.reason}
                      </span>
                      {/* What we declined to charge. The engine costed it before the
                          override stopped it, so the figure exists — and it's the whole
                          point of looking at this list. */}
                      {r.withheldTotal != null && (
                        <span className="shrink-0 whitespace-nowrap font-semibold">
                          {r.withheldQty ? `${r.withheldQty} × ` : ''}
                          {rate(r.withheldRate ?? 0, r.rateType)}
                          {' = '}
                          {money(r.withheldTotal)}
                        </span>
                      )}
                      {/* The same review state the lines above carry, and this list is
                          where it matters MOST: 95% of overrides decline a charge rather
                          than change one, so they have no row up there and this is the
                          only place they appear. Without it a decision taken at 1 × $50
                          sat here reading "$100 not billed" with nothing saying the
                          figure had moved (pro 5125289). */}
                      {r.overrideNeedsReview && (
                        <span
                          className="shrink-0 rounded-full bg-amber-200 px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-800/60 dark:text-amber-100"
                          title={
                            r.overrideStampedTotal != null
                              ? `Agreed at ${money(r.overrideStampedTotal)}, now ${money(r.withheldTotal ?? 0)}. Re-verify to approve this invoice.`
                              : 'The charge has changed since this was agreed. Re-verify to approve this invoice.'
                          }
                        >
                          Review
                        </span>
                      )}
                      {r.overrideNeedsReview && onReverify && !mainGroup?.invoiced && (
                        <button
                          type="button"
                          onClick={() => onReverify(r.item)}
                          title={`Re-verify ${r.item} at what it is worth now`}
                          aria-label={`Re-verify ${r.item}`}
                          className="shrink-0 rounded border border-amber-500 px-1.5 py-0.5 text-[0.5625rem] font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-500 dark:text-amber-100 dark:hover:bg-amber-900/40"
                        >
                          Re-verify
                        </button>
                      )}
                      {onOverride && !mainGroup?.invoiced && (
                        <OverrideEditButton
                          liitem={r.item}
                          title={r.reason}
                          onClick={() => onOverride(r.item, null)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {estimate.hasEstimates && (
                <p className="mt-3 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                  <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>
                    <span className="font-semibold">Some figures are estimated.</span>{' '}
                    This order isn&apos;t fully dated yet, so those lines are projected from
                    today — including any marked{' '}
                    <span className="font-semibold">Estimated</span> that happen to match
                    what&apos;s already billed, which is a coincidence rather than a
                    confirmation.{' '}
                    {stagesBlankRate ? (
                      <>
                        Staging adds them with the{' '}
                        <span className="font-semibold">rate left blank</span> — fill it
                        in once the real dates land.
                      </>
                    ) : (
                      <>
                        Staging bills them at the{' '}
                        <span className="font-semibold">projected figure</span>, which
                        re-rates itself once the real dates land.
                      </>
                    )}
                  </span>
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {/* Both sentences describe STAGING, so both go when there is nothing to stage
              — an archived estimate explaining how to load itself into a locked invoice
              is instructions for something the reader cannot do. The element itself
              stays: it holds the left half of a justify-between row, and dropping it
              would slide the Close button across. */}
          <p className="text-xs text-muted max-w-md">
            {!estimate?.archived && (mainBilled ? (
              hasOpenAccessorial && onStageAccessorial
                ? 'The main invoice is billed — stage an accessorial from its own panel.'
                : 'The main invoice is billed.'
            ) : (
              <>
                Staging loads these charges into the invoice lines — save to apply.
                {hasAccessorials && (onStageAccessorial
                  ? ' This button covers the main invoice; stage an accessorial from its own panel.'
                  : ' Accessorial charges are not included.')}
              </>
            ))}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-label hover:bg-highlight"
            >
              Close
            </button>
            {/* No staging action when the MAIN invoice is billed — the whole order or
                just it — not a disabled one. This button stages into the main invoice,
                whose line items are then locked, so a greyed button would advertise
                something that cannot happen here at all. */}
            {!estimate?.archived && !mainBilled && (
              <button
                onClick={() => estimate && onStage(estimate)}
                /* What this button can actually do is stage the MAIN invoice's changes.
                   It used to gate on estimate.hasChanges, which is true for an
                   accessorial-only change too — so on those orders it offered a staging
                   action that would have staged nothing. */
                disabled={!estimate || changeCount === 0}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Stage in form
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default InvoiceEstimateModal;
